import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import AppShell from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { haptic } from "@/lib/haptics";
import {
  ArrowLeft, Flame, Thermometer, Snowflake, Zap,
  Mail, Phone, FileText, CalendarPlus, Users, Check,
} from "lucide-react";

const SUGGESTIONS = {
  questions: [
    "Quel est votre volume mensuel ?",
    "Qui décide en interne ?",
    "Quel budget envisagez-vous ?",
    "Quelle est votre échéance ?",
    "Travaillez-vous déjà avec un prestataire ?",
    "Quelle est votre priorité n°1 ?",
    "Quel est votre point de douleur principal ?",
  ],
  answers: [
    "Projet imminent (< 3 mois)",
    "Budget déjà validé",
    "Concurrent actuel insatisfaisant",
    "Recherche active en cours",
    "En attente d'informations complémentaires",
    "Contact reporté à la rentrée",
    "Décideur disponible la semaine prochaine",
  ],
  objections: [
    "Budget serré",
    "Pas le bon moment",
    "Doit consulter son équipe",
    "Besoin d'un devis détaillé",
    "A déjà un prestataire",
    "Manque de temps",
    "Pas convaincu sur le ROI",
  ],
  note: [
    "Envoyer plaquette sous 48h",
    "Rappeler la semaine prochaine",
    "Inviter à un événement",
    "Introduire au décideur",
    "Préparer un devis personnalisé",
    "Faire une démo en visio",
  ],
};

const INTEREST_LEVELS = [
  { key: "Froid", icon: Snowflake, color: "from-sky-400 to-sky-600" },
  { key: "Tiède", icon: Thermometer, color: "from-amber-400 to-orange-500" },
  { key: "Chaud", icon: Flame, color: "from-orange-500 to-rose-500" },
  { key: "Très chaud", icon: Zap, color: "from-rose-500 to-orange-600" },
];

const NEXT_ACTION_PRESETS = [
  { key: "email", label: "Email de suivi", icon: Mail, color: "sky" },
  { key: "call", label: "Rappel téléphone", icon: Phone, color: "emerald" },
  { key: "proposal", label: "Envoyer un devis", icon: FileText, color: "amber" },
  { key: "rdv", label: "Planifier un RDV", icon: CalendarPlus, color: "violet" },
  { key: "visit", label: "Nouvelle visite", icon: Users, color: "fuchsia" },
];

export default function VisitReport() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [prospect, setProspect] = useState(null);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    interest_level: "",
    questions_asked: "",
    answers_noted: "",
    objections: "",
    next_actions: [],          // keys from NEXT_ACTION_PRESETS
    next_action_note: "",
    rdv_at: "",                // optional ISO datetime
    outcome: "",               // brief status summary
    duration: 15,              // minutes
  });

  useEffect(() => {
    if (!id) return;
    api.get(`/prospects/${id}`)
      .then((r) => setProspect(r.data.prospect))
      .catch(() => { toast.error("Lead introuvable"); navigate(-1); });
  }, [id, navigate]);

  const toggleAction = (key) => {
    setForm((f) => ({
      ...f,
      next_actions: f.next_actions.includes(key)
        ? f.next_actions.filter((k) => k !== key)
        : [...f.next_actions, key],
    }));
  };

  const save = async () => {
    if (!form.interest_level) {
      toast.error("Indiquez le niveau d'intérêt");
      return;
    }
    setLoading(true);
    try {
      // 1. Build rich notes combining all fields
      const noteBlocks = [];
      if (form.questions_asked.trim()) noteBlocks.push(`• Questions posées :\n${form.questions_asked.trim()}`);
      if (form.answers_noted.trim()) noteBlocks.push(`• Réponses notables :\n${form.answers_noted.trim()}`);
      if (form.objections.trim()) noteBlocks.push(`• Objections :\n${form.objections.trim()}`);
      if (form.next_action_note.trim()) noteBlocks.push(`• Plan d'action :\n${form.next_action_note.trim()}`);
      const notes = noteBlocks.join("\n\n");

      // 2. Create interaction of type "visit"
      await api.post("/interactions", {
        prospect_id: id,
        type: "visit",
        duration: Number(form.duration) || 15,
        outcome: form.outcome || `Intérêt ${form.interest_level.toLowerCase()}`,
        notes,
      });

      // 3. Patch lead: interest_level, rdv_at if any, status if RDV planned
      const patch = {
        interest_level: form.interest_level,
        notes: [prospect?.notes, notes].filter(Boolean).join("\n\n---\n"),
      };
      if (form.rdv_at) {
        patch.rdv_at = new Date(form.rdv_at).toISOString();
        patch.status = "RDV planifié";
      } else if (prospect?.status === "À contacter") {
        patch.status = "Contacté";
      }
      await api.put(`/prospects/${id}`, patch);

      // 4. Create scheduled actions for each selected next_action
      const scheduledDate = form.rdv_at
        ? new Date(form.rdv_at).toISOString()
        : new Date(Date.now() + 2 * 24 * 3600 * 1000).toISOString();
      for (const k of form.next_actions) {
        await api.post("/actions", {
          prospect_id: id,
          type: k,
          scheduled_date: scheduledDate,
          content: form.next_action_note || "",
          status: "pending",
        });
      }

      toast.success("Rapport ajouté à la fiche lead ✨");
      haptic("success");
      navigate(`/prospect/${id}`);
    } catch (e) {
      toast.error("Erreur à l'enregistrement");
    } finally {
      setLoading(false);
    }
  };

  if (!prospect) {
    return <AppShell><div className="grid place-items-center py-24"><div className="w-8 h-8 rounded-full border-2 border-emerald-500 border-t-transparent animate-spin" /></div></AppShell>;
  }

  return (
    <AppShell>
      {/* Header */}
      <div className="px-5 pt-5 pb-2 flex items-center gap-2">
        <button onClick={() => navigate(-1)} className="w-10 h-10 rounded-full grid place-items-center active:bg-[var(--surface-alt)]" data-testid="back" aria-label="Retour">
          <ArrowLeft className="w-5 h-5 text-zinc-300" />
        </button>
        <div className="flex-1">
          <div className="text-[11px] font-bold uppercase tracking-widest text-zinc-500">Rapport de visite</div>
          <h1 className="font-display font-bold text-[var(--text)]" data-testid="vr-title">{prospect.name || prospect.company}</h1>
        </div>
      </div>

      {/* Interest level — big emotion picker */}
      <div className="px-5 mt-4">
        <div className="text-[11px] font-bold uppercase tracking-widest text-zinc-400 mb-3 px-1">
          Niveau d'intérêt
        </div>
        <div className="grid grid-cols-4 gap-0 border border-[var(--border)]" data-testid="interest-picker">
          {INTEREST_LEVELS.map((l, i) => {
            const active = form.interest_level === l.key;
            const Icon = l.icon;
            return (
              <button
                key={l.key}
                onClick={() => setForm((f) => ({ ...f, interest_level: l.key }))}
                data-testid={`interest-${l.key}`}
                className={`aspect-square p-2 flex flex-col items-center justify-center gap-1.5 transition-colors ${
                  i > 0 ? "border-l border-[var(--border)]" : ""
                } ${
                  active
                    ? "bg-black text-white"
                    : "bg-[var(--surface)] text-[var(--text)] hover:bg-[var(--surface-alt)]/60"
                }`}
              >
                <Icon className="w-6 h-6" strokeWidth={2} />
                <span className="text-[11px] font-bold leading-none">{l.key}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Questions / Réponses / Objections */}
      <div className="px-5 mt-6">
        <div className="bg-[var(--surface)] rounded-xl shadow-soft divide-y divide-[var(--border-soft)]">
          <TextAreaRow
            label="Questions posées"
            value={form.questions_asked}
            onChange={(v) => setForm((f) => ({ ...f, questions_asked: v }))}
            testId="questions"
            placeholder="Ex: Quel est votre volume mensuel ?"
            suggestions={SUGGESTIONS.questions}
          />
          <TextAreaRow
            label="Réponses notables"
            value={form.answers_noted}
            onChange={(v) => setForm((f) => ({ ...f, answers_noted: v }))}
            testId="answers"
            placeholder="Ce que le lead a révélé sur ses besoins"
            suggestions={SUGGESTIONS.answers}
          />
          <TextAreaRow
            label="Objections rencontrées"
            value={form.objections}
            onChange={(v) => setForm((f) => ({ ...f, objections: v }))}
            testId="objections"
            placeholder="Ex: budget, timing, concurrent…"
            suggestions={SUGGESTIONS.objections}
          />
        </div>
      </div>

      {/* Next actions */}
      <div className="px-5 mt-6">
        <div className="text-[11px] font-bold uppercase tracking-widest text-zinc-500 mb-3 px-1">
          Actions à mener
        </div>
        <div className="grid grid-cols-2 gap-2.5">
          {NEXT_ACTION_PRESETS.map((a) => {
            const active = form.next_actions.includes(a.key);
            const colorMap = {
              violet: active ? "bg-emerald-500/100 text-white" : "bg-emerald-500/10 text-emerald-300",
              fuchsia: active ? "bg-orange-500 text-white" : "bg-orange-500/10 text-orange-300",
              emerald: active ? "bg-emerald-500/100 text-white" : "bg-emerald-500/10 text-emerald-300",
              amber: active ? "bg-amber-500 text-white" : "bg-amber-50 text-amber-700",
              sky: active ? "bg-sky-500 text-white" : "bg-sky-50 text-sky-700",
            };
            return (
              <button
                key={a.key}
                onClick={() => toggleAction(a.key)}
                data-testid={`next-${a.key}`}
                className={`rounded-2xl p-3 flex items-center gap-2.5 transition-all active:scale-95 ${colorMap[a.color]}`}
              >
                <div className="w-8 h-8 rounded-xl bg-white/50 grid place-items-center">
                  {active ? <Check className="w-4 h-4" strokeWidth={3} /> : <a.icon className="w-4 h-4" />}
                </div>
                <span className="text-[13px] font-semibold text-left leading-tight">{a.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* RDV scheduler */}
      {form.next_actions.includes("rdv") && (
        <div className="px-5 mt-5" data-testid="rdv-scheduler">
          <div className="text-[11px] font-bold uppercase tracking-widest text-zinc-500 mb-2 px-1">
            Date du rendez-vous
          </div>
          <div className="bg-[var(--surface)] rounded-xl p-4 shadow-soft">
            <Input
              type="datetime-local"
              value={form.rdv_at}
              onChange={(e) => setForm((f) => ({ ...f, rdv_at: e.target.value }))}
              className="rounded-2xl h-12 border-0 bg-[var(--surface-alt)]"
              data-testid="rdv-input"
            />
          </div>
        </div>
      )}

      {/* Plan note */}
      <div className="px-5 mt-5">
        <div className="text-[11px] font-bold uppercase tracking-widest text-zinc-500 mb-2 px-1">
          Notes complémentaires
        </div>
        <div className="bg-[var(--surface)] rounded-xl p-4 shadow-soft">
          <Textarea
            rows={3}
            value={form.next_action_note}
            onChange={(e) => setForm((f) => ({ ...f, next_action_note: e.target.value }))}
            placeholder="Précisez le contenu de vos prochaines actions, ce qui a été convenu, etc."
            className="rounded-2xl border-0 bg-[var(--surface-alt)] resize-none"
            data-testid="note"
          />
          <SuggestionChips
            options={SUGGESTIONS.note}
            onPick={(p) => setForm((f) => ({ ...f, next_action_note: appendPhrase(f.next_action_note, p) }))}
            testId="suggestion-note"
          />
        </div>
      </div>

      {/* Duration */}
      <div className="px-5 mt-5">
        <div className="bg-[var(--surface)] rounded-xl p-4 shadow-soft flex items-center justify-between gap-4">
          <Label className="text-sm font-semibold text-zinc-300">Durée de la visite</Label>
          <div className="flex items-center gap-2">
            <Input
              type="number"
              min="1"
              value={form.duration}
              onChange={(e) => setForm((f) => ({ ...f, duration: e.target.value }))}
              className="rounded-2xl h-10 w-20 text-center"
              data-testid="duration"
            />
            <span className="text-xs font-semibold text-zinc-500">min</span>
          </div>
        </div>
      </div>

      {/* Save button */}
      <div className="px-5 mt-8 pb-24">
        <Button
          onClick={save}
          disabled={loading}
          data-testid="save-report"
          className="w-full rounded-full bg-gradient-to-br from-emerald-500 to-orange-500 hover:opacity-90 h-12 font-semibold text-[15px] shadow-floating"
        >
          {loading ? "Enregistrement…" : "Ajouter à la fiche prospect"}
        </Button>
        {/* Générer email de relance — Phase 1 Auto-email */}
        <GenerateEmailButton
          prospect={prospect}
          voiceDebrief={[form.questions_asked, form.answers_noted, form.next_action_note].filter(Boolean).join(" — ")}
        />
        <button
          onClick={() => navigate(`/prospect/${id}`)}
          className="w-full mt-3 text-sm text-zinc-500 font-medium"
          data-testid="skip-report"
        >
          Passer pour l'instant
        </button>
      </div>
    </AppShell>
  );
}

function appendPhrase(existing, phrase) {
  const cur = (existing || "").trim();
  if (cur.toLowerCase().includes(phrase.toLowerCase())) return cur; // don't duplicate
  return cur ? `${cur}\n• ${phrase}` : `• ${phrase}`;
}

function SuggestionChips({ options, onPick, testId }) {
  if (!options?.length) return null;
  return (
    <div className="flex flex-wrap gap-1.5 mt-2.5" data-testid={testId}>
      {options.map((p) => (
        <button
          key={p}
          type="button"
          onClick={() => onPick(p)}
          className="text-[11px] font-semibold bg-emerald-500/10 hover:bg-emerald-500/15 text-emerald-300 rounded-full px-2.5 py-1 active:scale-95 transition-transform border border-emerald-500/30"
        >
          + {p}
        </button>
      ))}
    </div>
  );
}

function TextAreaRow({ label, value, onChange, placeholder, testId, suggestions }) {
  return (
    <div className="p-4">
      <Label className="text-[11px] font-bold uppercase tracking-widest text-zinc-500">{label}</Label>
      <Textarea
        rows={2}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="rounded-2xl border-0 bg-[var(--surface-alt)] mt-1.5 resize-none text-sm"
        data-testid={testId}
      />
      <SuggestionChips
        options={suggestions}
        onPick={(p) => onChange(appendPhrase(value, p))}
        testId={`suggestions-${testId}`}
      />
    </div>
  );
}

function GenerateEmailButton({ prospect, voiceDebrief }) {
  const [open, setOpen] = React.useState(false);
  const [draft, setDraft] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [scheduling, setScheduling] = React.useState(false);
  const [to, setTo] = React.useState("");

  const generate = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/emails/generate", {
        prospect_id: prospect.prospect_id,
        trigger: "visit",
        voice_debrief: voiceDebrief || "",
      });
      setDraft(data);
      setTo(data.to_email || prospect.email || "");
      setOpen(true);
      if (data.ai_available === false) {
        toast.warning("IA indisponible — brouillon type proposé");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Génération impossible");
    } finally { setLoading(false); }
  };

  const schedule = async () => {
    if (!to) { toast.error("Email destinataire requis"); return; }
    setScheduling(true);
    try {
      await api.post("/emails/schedule", {
        prospect_id: prospect.prospect_id,
        to_email: to, to_name: prospect.name || "",
        subject: draft.subject,
        body_text: draft.body_text,
        body_html: draft.body_html || "",
        trigger: "visit",
      });
      toast.success("Email programmé — vous pouvez annuler dans la barre en bas");
      setOpen(false);
      setDraft(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur programmation (SMTP non configuré ?)");
    } finally { setScheduling(false); }
  };

  return (
    <>
      <button
        onClick={generate}
        disabled={loading}
        data-testid="visit-generate-email"
        className="mt-3 w-full rounded-full border border-[var(--border)] bg-[var(--surface)] h-12 text-sm font-semibold hover:bg-emerald-500 hover:text-white transition-colors"
      >
        {loading ? "Génération de l'email…" : "📧 Générer un email de relance"}
      </button>

      {open && draft && (
        <div className="fixed inset-0 z-50 bg-black/60 grid place-items-center p-4" onClick={() => setOpen(false)}>
          <div className="bg-[var(--surface)] rounded-xl max-w-xl w-full max-h-[85vh] overflow-auto p-6" onClick={(e) => e.stopPropagation()}>
            <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-400">
              Brouillon · {draft.ai_available ? "Généré par Prosperr" : "Modèle de secours"}
            </div>
            <h3 className="mt-1 font-display font-bold text-xl">{draft.subject}</h3>
            <div className="mt-4">
              <label className="text-[11px] font-bold uppercase tracking-widest text-zinc-400">Destinataire</label>
              <input
                value={to} onChange={(e) => setTo(e.target.value)}
                placeholder="prospect@entreprise.fr"
                data-testid="visit-email-to"
                className="mt-1 w-full border border-[var(--border)] rounded-md h-11 px-3 text-sm"
              />
            </div>
            <div className="mt-4">
              <label className="text-[11px] font-bold uppercase tracking-widest text-zinc-400">Sujet</label>
              <input
                value={draft.subject}
                onChange={(e) => setDraft({ ...draft, subject: e.target.value })}
                className="mt-1 w-full border border-[var(--border)] rounded-md h-11 px-3 text-sm"
              />
            </div>
            <div className="mt-4">
              <label className="text-[11px] font-bold uppercase tracking-widest text-zinc-400">Corps</label>
              <textarea
                value={draft.body_text}
                onChange={(e) => setDraft({ ...draft, body_text: e.target.value, body_html: "" })}
                rows={10}
                className="mt-1 w-full border border-[var(--border)] rounded-md p-3 text-sm font-mono"
                data-testid="visit-email-body"
              />
            </div>
            <div className="mt-5 flex gap-2 justify-end">
              <button onClick={() => setOpen(false)} className="rounded-full border border-[var(--border)] h-11 px-5 text-sm">Annuler</button>
              <button
                onClick={schedule}
                disabled={scheduling}
                data-testid="visit-email-schedule"
                className="rounded-full bg-black text-white h-11 px-5 text-sm font-semibold hover:bg-black/85"
              >
                {scheduling ? "…" : "Programmer l'envoi"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
